import {
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
  createContext,
  useCallback,
  useContext,
} from 'react';

import { useCardContext } from '@/microfrontends/listing/shared/context/CardContext';
import { cardOfferDataSelector } from '@/microfrontends/listing/shared/context/selectors';

import { noop } from '@/packages/utils';

import { useCompareOfferAction } from '../../../hooks/card/useCompareOfferAction';
import { useComplaintToOfferAction } from '../../../hooks/card/useComplaintToOfferAction';
import { useDownloadPDF } from '../../../hooks/card/useDownloadPDF';
import { useHideOffer } from '../../../hooks/card/useHideOffer';
import { GalleryActions } from '../components/GalleryAcitons';

const GalleryActionsMenuContext = createContext<() => void>(noop);

export function GalleryActionsMenuContainer({
  ref,
  onItemClick,
}: {
  ref?: Ref<HTMLUListElement>;
  onItemClick(): void;
}): ReactNode {
  const cardContext = useCardContext();
  const { comparisonStatus } = cardOfferDataSelector(cardContext);
  const isUnavailable = !comparisonStatus || comparisonStatus.status === 'unavailable';

  return (
    <GalleryActionsMenuContext.Provider value={onItemClick}>
      <GalleryActions ref={ref}>
        {!isUnavailable && <CompareAction title={comparisonStatus.description} />}
        <HideOfferAction />
        <ComplaintAction />
        <DownloadPdfAction />
      </GalleryActions>
    </GalleryActionsMenuContext.Provider>
  );
}

type CompareActionProps = {
  title: string;
};

function CompareAction({ title }: PropsWithChildren<CompareActionProps>): ReactNode {
  const сompareOffer = useCompareOfferAction();
  const onItemClick = useContext(GalleryActionsMenuContext);

  const handleCompareOffer = useCallback(
    (e: SyntheticEvent): void => {
      e.stopPropagation();
      сompareOffer();
      onItemClick();
    },
    [onItemClick, сompareOffer],
  );

  return <GalleryActions.Item onClick={handleCompareOffer}>{title}</GalleryActions.Item>;
}

function HideOfferAction(): ReactNode {
  const hideOffer = useHideOffer();
  const onItemClick = useContext(GalleryActionsMenuContext);

  return (
    <GalleryActions.Item
      onClick={(e: SyntheticEvent) => {
        e.stopPropagation();
        hideOffer();
        onItemClick();
      }}
    >
      Скрыть объявление
    </GalleryActions.Item>
  );
}

function ComplaintAction(): ReactNode {
  const complaitToOffer = useComplaintToOfferAction();
  const onItemClick = useContext(GalleryActionsMenuContext);

  const handleComplaint = useCallback(
    (e: SyntheticEvent): void => {
      e.stopPropagation();
      complaitToOffer();
      onItemClick();
    },
    [onItemClick, complaitToOffer],
  );

  return <GalleryActions.Item onClick={handleComplaint}>Пожаловаться</GalleryActions.Item>;
}

function DownloadPdfAction(): ReactNode {
  const downloadPDF = useDownloadPDF();
  const onItemClick = useContext(GalleryActionsMenuContext);

  const handleDownload = useCallback(
    (e: SyntheticEvent): void => {
      e.stopPropagation();
      downloadPDF();
      onItemClick();
    },
    [onItemClick, downloadPDF],
  );

  return <GalleryActions.Item onClick={handleDownload}>Скачать PDF</GalleryActions.Item>;
}
